import Modal from "react-modal";
import UI from "@mile-meeting/mile-ui";
import {
  ButtonContainer,
  Content,
  CountContainer,
  ErrorContainer,
  InfoContainer,
  InviteInput,
  ModalContent,
  ModalHeader,
  ModalSection,
  Table,
  TableContent,
  TableIndex,
} from "./index.styles";
import { roleStringList, roleStringListRemovedOwner, roleToString, stringToRole, UserRole } from "utils/role";
import UserInput from "./user-input";
import { useEffect, useState } from "react";
import { registerMemberList, sendInviteMember } from "apis/member-invites/member-invites";
import UserItem from "./user-item";
import { use } from "marked";
import { useRecoilValue } from "recoil";
import { member, workspace } from "recoil/recoilState";
import { openGooglePopup } from "./google-oauth-modal";
import { useToast } from "hook/useToast";
import ToolTip from "uis/new-tootip";
import { register } from "validatorjs";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    width: "832px",
    marginRight: "-50%",
    borderRadius: "16px",
    transform: "translate(-50%, -50%)",
    padding: 0,
    overflow: "visible",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  onUpdate?: () => void;
};

const enableInvateButton = (value: string[]) => {
  if (value.length > 0) {
    return true;
  } else return false;
};

export type RegisterMemberListResponse = {
  role: UserRole;
  email: string;
  validationResult: boolean;
  errorMessage?: string;
};

export default function InviteModal(props: Props) {
  const { showToast } = useToast();
  const { modalIsOpen, closeModal, onUpdate } = props;
  const [inputUsers, setInputUsers] = useState<string[]>([]);
  const [role, setRole] = useState<UserRole>(UserRole.Staff);
  const [registerResponse, setRegisterResponse] = useState<RegisterMemberListResponse[]>([]);
  const workspaceInfo = useRecoilValue(workspace);
  const memberInfo = useRecoilValue(member);

  useEffect(() => {
    if (!modalIsOpen) {
      setInputUsers([]);
      setRegisterResponse([]);
      setRole(UserRole.Staff);
    }
  }, [modalIsOpen]);

  const onHandleInvite = async () => {
    if (registerResponse.length !== 0) {
      const memberList = registerResponse
        .filter((item) => item.validationResult)
        .map((item) => ({ email: item.email, role: item.role }));

      if (workspaceInfo && workspaceInfo?.limitInvitation < workspaceInfo?.sendInvitation + memberList.length) {
        showToast("1일 전송 가능 이메일 양을 초과했습니다.", "warning");

        return;
      } else {
        const { success, status } = await sendInviteMember(memberList);
        if (success) {
          showToast("멤버에게 초대 이메일을 전송하였습니다.", "success");
          onUpdate?.();
          setRegisterResponse([]);
        } else {
          if (status === 409) {
            showToast("1일 전송 가능 이메일 양을 초과했습니다.", "warning");
          }
        }
      }
    }
  };
  const registerMembers = async (users: string[], role: UserRole) => {
    const uniqueUsers = Array.from(new Set(users));
    const realRegisterResponse = registerResponse.filter((item) => item.validationResult);
    const slicedUsers = uniqueUsers.slice(0, 50 - realRegisterResponse.length);
    if (slicedUsers.length === 0) return;

    const { success, invites, message } = await registerMemberList(role, slicedUsers);
    if (success && invites) {
      const unique = Array.from(new Map([...registerResponse, ...invites].map((item) => [item.email, item])).values());
      setRegisterResponse(unique);
      setInputUsers([...uniqueUsers.slice(50 - realRegisterResponse.length)]);
    } else {
      showToast(message ?? "멤버 초대 등록에 실패하였습니다. 다시 시도해주세요.", "error");
    }
  };

  const onChangeRole = (index: number, role: number) => {
    const newRegisterResponse = registerResponse.map((item, i) => (i === index ? { ...item, role } : item));
    setRegisterResponse(newRegisterResponse);
  };

  const onDeleteItem = (index: number) => {
    const newRegisterResponse = registerResponse.filter((_, i) => i !== index);
    setRegisterResponse(newRegisterResponse);
  };

  const onHandleByGoogleWorkspace = (success: boolean, emails: string[], message?: string) => {
    if (!success) {
      showToast(message ?? "멤버 목록을 가져오는데 실패하였습니다. 다시 시도해주세요.", "error");
    } else {
      if (emails.length === 0) {
        showToast("불러올 수 있는 멤버가 없습니다.", "warning");
      } else {
        setInputUsers(emails);
      }
    }
  };

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <ModalHeader>
        <UI.Typography type="heading02" fontStyle="bold" color="var(--alias-color-label-neutral)">
          멤버 초대
        </UI.Typography>
        <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-alternative)">
          ※ 초대 링크는 발송일로부터 7일 이후 만료됩니다.
        </UI.Typography>
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
          }}
        >
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </div>
      </ModalHeader>
      <ModalSection>
        <ModalContent>
          <InfoContainer>
            <UI.Icon name="info" color="var(--alias-color-primary-normal" size={24} />
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal">
              쉼표, 공백으로 구분된 이메일 또는 엑셀에서 이메일이 포함된 열을 복사, 붙여넣기 하여 한 번에 추가가
              가능합니다. 초대 이메일이 수신되지 않은 경우, 수신자에게 스팸 메일함 확인을 요청해 주세요.
            </UI.Typography>
          </InfoContainer>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <InviteInput>
              <UserInput onChangeValue={setInputUsers} value={inputUsers} />
              <UI.SelectBox
                width={160}
                onChange={(value) => {
                  setRole(stringToRole(value[0]));
                }}
                selectedData={[roleToString(role)]}
                data={memberInfo?.role === UserRole.Owner ? roleStringList : roleStringListRemovedOwner}
              />
              <UI.Button
                onClick={() => registerMembers(inputUsers, role)}
                title="등록"
                primary={false}
                size="large"
                disabled={!enableInvateButton(inputUsers)}
                style={{ width: "76px", whiteSpace: "nowrap" }}
              />
            </InviteInput>
            <UI.ButtonGroups.Text
              onClick={() => openGooglePopup(false, onHandleByGoogleWorkspace)}
              styles={{
                container: {
                  marginRight: "auto",
                },
              }}
              title="Google Workspace의 멤버 목록 불러오기"
              size="medium"
              type="secondary"
              prefixIcon="google"
              showPrefixIcon
            />
          </div>
          <Content>
            <Table>
              <TableIndex>
                <UI.Typography
                  type="label01"
                  fontStyle="medium"
                  color="var(--alias-color-label-normal)"
                  style={{ flex: "1", width: "240px" }}
                >
                  이메일
                </UI.Typography>
                <UI.Typography
                  type="label01"
                  fontStyle="medium"
                  color="var(--alias-color-label-normal)"
                  style={{ width: "222px" }}
                >
                  권한
                </UI.Typography>
              </TableIndex>
              <TableContent>
                {registerResponse.map((item, index) => (
                  <UserItem
                    key={index}
                    user={item}
                    onDelete={() => onDeleteItem(index)}
                    onChangeRole={(role) => onChangeRole(index, role)}
                  />
                ))}
              </TableContent>
            </Table>
            <ButtonContainer>
              {registerResponse.filter((item) => item.validationResult === false).length > 0 && (
                <ErrorContainer>
                  <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <UI.Icon name="inform" size={20} color="var(--alias-color-status-negative)" />
                    <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-status-negative)">
                      {registerResponse.filter((item) => item.validationResult === false).length}개의 오류
                    </UI.Typography>
                  </div>
                  <div onClick={() => setRegisterResponse(registerResponse.filter((item) => item.validationResult))}>
                    <UI.Typography
                      style={{ textDecoration: "underline", cursor: "pointer" }}
                      type="body01 normal"
                      fontStyle="medium"
                      color="var(--alias-color-label-neutral)"
                    >
                      오류 발생 항목 모두 제거
                    </UI.Typography>
                  </div>
                </ErrorContainer>
              )}
              <CountContainer>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-normal)">
                  {registerResponse.filter((item) => item.validationResult).length}
                </UI.Typography>
                <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-alternative)">
                  /
                </UI.Typography>
                <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-alternative)">
                  50
                </UI.Typography>
                <div style={{ position: "relative", cursor: "pointer" }}>
                  <ToolTip
                    text="1회 최대 전송량은 50개이며, 1일 최대 전송량은 2,000개입니다."
                    position={{
                      top: 20,
                      left: -171,
                    }}
                  />
                </div>
              </CountContainer>
            </ButtonContainer>
          </Content>
        </ModalContent>
        <UI.Button
          title={registerResponse.filter((item) => item.validationResult).length + "명 초대하기"}
          disabled={!registerResponse.some((item) => item.validationResult)}
          size="large"
          onClick={onHandleInvite}
        />
      </ModalSection>
    </Modal>
  );
}
